import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import WhatsAppChatView from './WhatsAppChatView';

function AlcazarAgente() {
  const { user } = useAuth();
  const isEditor = user && ['superadmin', 'admin'].includes(user.role);

  const [supervised, setSupervised] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const editingIdRef = useRef(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [editResponse, setEditResponse] = useState('');
  const [saveTopic, setSaveTopic] = useState('');
  const [saveToKb, setSaveToKb] = useState(true);
  const [sending, setSending] = useState(null);
  const [translated, setTranslated] = useState({});
  const [translatingId, setTranslatingId] = useState(null);
  const [subTab, setSubTab] = useState('pending');
  const [historyPhone, setHistoryPhone] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [kbTopics, setKbTopics] = useState([]);
  const [modifiedItems, setModifiedItems] = useState([]);
  const [savingKbId, setSavingKbId] = useState(null);
  const [followupText, setFollowupText] = useState({});
  const [sendingFollowup, setSendingFollowup] = useState(null);
  const [kbTopicInput, setKbTopicInput] = useState('');
  const [kbFaqInput, setKbFaqInput] = useState('');
  const [topicOverride, setTopicOverride] = useState({});
  const [sentKbTopic, setSentKbTopic] = useState({});
  const [savingSentKb, setSavingSentKb] = useState(null);
  const [responseOverride, setResponseOverride] = useState({});
  const [channelFilter, setChannelFilter] = useState('all');

  useEffect(() => { editingIdRef.current = editingId; }, [editingId]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Cuando cambias el topic, cargar el contenido de ese topic de la KB
  const handleTopicChange = async (itemId, topicName) => {
    setTopicOverride(prev => ({...prev, [itemId]: topicName}));
    if (!topicName) return;
    try {
      const res = await api.get('/api/alcazar/knowledge/' + encodeURIComponent(topicName) + '?lang=es');
      if (res.data?.topic?.content) {
        setResponseOverride(prev => ({...prev, [itemId]: res.data.topic.content}));
        // Update edited_response in the queue so it sends the right content
        await api.put('/api/alcazar/agente/' + itemId + '/edit-response', { edited_response: res.data.topic.content });
      }
    } catch(e) { /* topic not found, keep original */ }
  };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/alcazar/agente/status');
      if (res.data.success) setSupervised(res.data.supervised);
    } catch {}
  }, []);

  const fetchQueue = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const status = subTab === 'sent' ? 'sent' : 'pending';
      const res = await api.get('/api/alcazar/agente/queue', { params: { status } });
      if (res.data.success) setItems(res.data.items || []);
    } catch (err) {
      if (err.response?.status !== 401 && !silent) showToast('Error al cargar cola', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [subTab]);

  const fetchModified = useCallback(async () => {
    try {
      const res = await api.get('/api/alcazar/agente/modified');
      if (res.data.success) setModifiedItems(res.data.items || []);
    } catch {}
  }, []);

  const fetchTopics = useCallback(async () => {
    try {
      const res = await api.get('/api/alcazar/knowledge', { params: { lang: 'es' } });
      if (res.data?.topics) setKbTopics(res.data.topics.map(t => ({id: t.id, topic: t.topic})));
    } catch {}
  }, []);

  useEffect(() => { fetchStatus(); fetchTopics(); }, [fetchStatus, fetchTopics]);

  useEffect(() => {
    if (subTab === 'modified') fetchModified();
    else fetchQueue();
  }, [subTab, fetchQueue, fetchModified]);

  // Sound notification for new messages
  const prevCountRef = useRef(0);
  const playNotification = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === 'suspended') { ctx.resume(); }
      const beep = (freq, start, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'square';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(0.9, ctx.currentTime + start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + dur + 0.02);
      };
      beep(880, 0, 0.25);
    } catch {}
  }, []);

  // Auto-refresh with sound notification
  useEffect(() => {
    const interval = setInterval(() => {
      if (editingIdRef.current) return;
      if (subTab === 'modified') fetchModified();
      else fetchQueue(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [subTab, fetchQueue, fetchModified]);

  // Play sound when new pending items arrive
  useEffect(() => {
    var pendingCount = items.filter(i => i.status === 'pending').length;
    if (pendingCount > prevCountRef.current && supervised) {
      playNotification();
    }
    prevCountRef.current = pendingCount;
  }, [items, supervised, playNotification]);

  const toggleMode = async () => {
    try {
      const res = await api.post('/api/alcazar/agente/toggle', { enabled: !supervised });
      if (res.data.success) {
        setSupervised(res.data.supervised);
        showToast(res.data.supervised ? 'Modo supervisado ACTIVADO' : 'Modo automatico ACTIVADO');
      }
    } catch { showToast('Error al cambiar modo', 'error'); }
  };

  const translateText = async (id, text) => {
    setTranslatingId(id);
    try {
      const res = await api.post('/api/alcazar/translate-text', { text, target_lang: 'es' });
      if (res.data.success) setTranslated(prev => ({ ...prev, [id]: res.data.translated }));
    } catch { showToast('Error al traducir', 'error'); }
    finally { setTranslatingId(null); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditQuestion(item.edited_question || item.question_es || item.original_question || '');
    setEditResponse(item.edited_response || item.xia_response_es || '');
    setSaveTopic(item.xia_intent || '');
    setSaveToKb(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditQuestion('');
    setEditResponse('');
    setSaveTopic('');
  };

  const saveEdit = async () => {
    try {
      await api.put('/api/alcazar/agente/' + editingId + '/edit', {
        edited_question: editQuestion,
        edited_response: editResponse,
      });
      showToast('Editado');
    } catch { showToast('Error al editar', 'error'); }
  };

  const sendItem = async (id) => {
    setSending(id);
    try {
      const item = items.find(i => i.id === id);
      const res = await api.post('/api/alcazar/agente/' + id + '/send', {
        save_to_kb: saveToKb,
        kb_topic: saveTopic || item?.xia_intent || null,
      });
      if (res.data.success) {
        showToast(res.data.message);
        cancelEdit();
        fetchQueue();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al enviar', 'error');
    } finally { setSending(null); }
  };

  const sendFollowup = async (item) => {
    const text = (followupText[item.id] || '').trim();
    if (!text) return;
    setSendingFollowup(item.id);
    try {
      const res = await api.post('/api/alcazar/agente/send-followup', {
        sender_id: item.sender_id,
        channel: item.channel,
        message: text,
        email_message_id: item.email_message_id || null,
      });
      if (res.data && res.data.success) {
        showToast('Mensaje enviado al cliente');
        setFollowupText(prev => ({ ...prev, [item.id]: '' }));
      } else {
        showToast((res.data && res.data.error) || 'Error al enviar', 'error');
      }
    } catch (err) {
      showToast((err.response && err.response.data && err.response.data.error) || 'Error al enviar', 'error');
    } finally { setSendingFollowup(null); }
  };

  const rejectItem = async (id) => {
    try {
      await api.post('/api/alcazar/agente/' + id + '/reject');
      showToast('Rechazado');
      fetchQueue();
    } catch { showToast('Error', 'error'); }
  };

  const openHistory = async (phone) => {
    setHistoryPhone(phone);
    setHistoryLoading(true);
    try {
      const res = await api.get('/api/alcazar/agente/history/' + encodeURIComponent(phone));
      if (res.data.success) setHistory(res.data.wa_history || []);
    } catch { showToast('Error al cargar historial', 'error'); }
    finally { setHistoryLoading(false); }
  };

  const saveSentToKb = async (id, fallbackTopic) => {
    const topic = ((sentKbTopic[id] !== undefined ? sentKbTopic[id] : fallbackTopic) || '').trim();
    if (!topic) { showToast('Escribe un topic', 'error'); return; }
    setSavingSentKb(id);
    try {
      const res = await api.post('/api/alcazar/agente/' + id + '/save-to-kb', { kb_topic: topic });
      if (res.data.success) {
        showToast(res.data.message || 'Guardado en KB');
        fetchQueue(true);
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Error', 'error');
    } finally { setSavingSentKb(null); }
  };

  const saveModifiedToKb = async (id) => {
    if (!kbTopicInput) { showToast('Escribe un topic', 'error'); return; }
    setSavingKbId(id);
    try {
      const res = await api.post('/api/alcazar/agente/' + id + '/save-to-kb', {
        kb_topic: kbTopicInput,
        faq_question: kbFaqInput || null,
      });
      if (res.data.success) {
        showToast(res.data.message);
        setKbTopicInput('');
        setKbFaqInput('');
        fetchModified();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Error', 'error');
    } finally { setSavingKbId(null); }
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const cleanText = (s) => {
    if (!s) return '';
    return s.replace(/&nbsp;/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
      .replace(/-{3,}\s*Forwarded message[\s\S]*?Subject:[^\n]*/gi, '')
      .replace(/^From:.*$/gm, '').replace(/^To:.*$/gm, '').replace(/^Date:.*$/gm, '')
      .replace(/\n{3,}/g, '\n\n').trim();
  };



  const renderItem = (item, isModified = false) => (
    <div key={item.id} className={'bg-white rounded-xl shadow border-l-4 ' + (
      item.status === 'sent' ? 'border-green-400' :
      item.status === 'rejected' ? 'border-red-400 opacity-50' :
      'border-amber-400'
    )}>
      <div className="px-4 py-3 flex items-center gap-3 border-b border-gray-100 flex-wrap">
        <span className={'px-2 py-0.5 rounded text-xs font-medium ' + (
          item.channel === 'whatsapp' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
        )}>{item.channel === 'whatsapp' ? 'WhatsApp' : 'Email'}</span>
        <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
        <span className="text-sm text-gray-700">{item.sender_id || item.sender_name || '-'}</span>
        {item.original_language && item.original_language !== 'es' && (
          <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-mono">{item.original_language.toUpperCase()}</span>
        )}
        {item.xia_intent && (
          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-mono">{item.xia_intent}</span>
        )}
        {(item.metadata?.kb_id || item.metadata?.kb_topic) && (
          <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-mono" title="ID respuesta en Conocimiento">KB #{item.metadata.kb_id || '?'} ({item.metadata.kb_topic})</span>
        )}
        {item.sender_id && (
          <button onClick={() => openHistory(item.sender_id)}
            className="ml-auto px-2.5 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-xs hover:bg-indigo-200 flex items-center gap-1.5 transition"
            title="Ver historial completo">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            Historial
          </button>
        )}
      </div>

      <div className="px-4 pt-2 flex gap-2 items-center flex-wrap" onKeyDown={e => e.stopPropagation()}>
        {((item.metadata && item.metadata.attachments) || []).map((att, i) => (
          <a key={'fa' + i} href={'/api/alcazar/attachment/' + att.filename + '?token=' + (localStorage.getItem('token') || '')} target="_blank" rel="noreferrer"
            className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg text-sm font-medium hover:bg-amber-200 transition whitespace-nowrap flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/></svg>
            Ver adjunto
          </a>
        ))}
        <input type="text" value={followupText[item.id] || ''}
          onChange={e => setFollowupText(prev => ({ ...prev, [item.id]: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter') sendFollowup(item); }}
          placeholder="Enviar otro mensaje al cliente..."
          className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none" />
        <button onClick={() => sendFollowup(item)} disabled={sendingFollowup === item.id || !(followupText[item.id] || '').trim()}
          className="px-4 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 transition shadow-sm">
          {sendingFollowup === item.id ? 'Enviando...' : 'Enviar'}
        </button>
      </div>

      {editingId === item.id ? (
        <div className="p-4 space-y-3" onKeyDown={e => e.stopPropagation()} onDragStart={e => e.preventDefault()} onDrop={e => e.preventDefault()}>
          <div>
            <label className="block text-xs text-gray-600 font-semibold mb-1">PREGUNTA (editable)</label>
            <textarea value={editQuestion} onChange={e => setEditQuestion(e.target.value)}
              className="w-full border-2 border-blue-300 rounded-xl p-3 text-sm min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 font-semibold mb-1">RESPUESTA (editable)</label>
            <textarea value={editResponse} onChange={e => setEditResponse(e.target.value)}
              className="w-full border-2 border-green-300 rounded-xl p-3 text-sm min-h-[120px] focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={async () => { await saveEdit(); sendItem(item.id); }} disabled={sending === item.id}
              className="px-5 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-50 transition shadow">
              {sending === item.id ? 'Enviando...' : 'Enviar + Traducir'}
            </button>
            <button onClick={() => rejectItem(item.id)}
              className="px-4 py-2.5 bg-red-100 text-red-700 rounded-xl text-sm hover:bg-red-200 transition">
              Rechazar
            </button>
            <button onClick={cancelEdit}
              className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-300 transition">
              Cancelar
            </button>
          </div>
        </div>
      ) : isModified ? (
        <div className="p-4 space-y-3" onKeyDown={e => e.stopPropagation()}>
          <div className="mb-2">
            <div className="text-xs text-gray-500 font-semibold mb-1">PREGUNTA ORIGINAL {item.original_language && item.original_language !== 'es' && <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-mono ml-1">{item.original_language.toUpperCase()}</span>}</div>
            <div className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded-lg p-2">{cleanText(item.original_question || '-')}</div>
            {!translated['mod_q_' + item.id] && (
              <button onClick={() => translateText('mod_q_' + item.id, item.original_question)}
                disabled={translatingId === 'mod_q_' + item.id}
                className="mt-2 px-3 py-1.5 bg-yellow-400 text-yellow-900 border border-yellow-500 rounded-lg text-xs font-semibold hover:bg-yellow-300 disabled:opacity-50 transition">
                {translatingId === 'mod_q_' + item.id ? 'Traduciendo...' : 'Traducir'}
              </button>
            )}
            {translated['mod_q_' + item.id] && (
              <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-sm"><span className="text-xs font-semibold text-yellow-700 block mb-1">ES:</span>{translated['mod_q_' + item.id]}</div>
            )}
          </div>
          <div className="mb-2 bg-green-50 rounded-lg p-3">
            <div className="text-xs text-green-700 font-semibold mb-1">RESPUESTA EDITADA POR {(item.reviewed_by || '').split('@')[0].toUpperCase()}</div>
            <div className="text-sm text-gray-800 whitespace-pre-wrap">{item.edited_response || '-'}</div>
            {!translated['mod_r_' + item.id] && (
              <button onClick={() => translateText('mod_r_' + item.id, item.edited_response)}
                disabled={translatingId === 'mod_r_' + item.id}
                className="mt-2 px-3 py-1.5 bg-yellow-400 text-yellow-900 border border-yellow-500 rounded-lg text-xs font-semibold hover:bg-yellow-300 disabled:opacity-50 transition">
                {translatingId === 'mod_r_' + item.id ? 'Traduciendo...' : 'Traducir'}
              </button>
            )}
            {translated['mod_r_' + item.id] && (
              <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-sm"><span className="text-xs font-semibold text-yellow-700 block mb-1">ES:</span>{translated['mod_r_' + item.id]}</div>
            )}
          </div>
          <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 space-y-3">
            <div className="text-xs font-bold text-amber-700 uppercase">Asociar a Conocimiento</div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Topic</label>
              <input type="text" value={kbTopicInput} onChange={e => setKbTopicInput(e.target.value)}
                list="topic-suggestions"
                placeholder="Escribe o selecciona..."
                className="w-full border-2 border-amber-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 outline-none" />
              <datalist id="topic-suggestions">
                {kbTopics.map(t => <option key={t.id || t} value={t.topic || t}>{(t.id || '') + ' - ' + (t.topic || t)}</option>)}
              </datalist>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Pregunta generica (opcional)</label>
              <input type="text" value={kbFaqInput} onChange={e => setKbFaqInput(e.target.value)}
                placeholder="Ej: Se puede entrar con perros?"
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 outline-none" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setSaveTopic(kbTopicInput); setSaveToKb(true); saveModifiedToKb(item.id); }} disabled={savingKbId === item.id || !kbTopicInput}
                className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition shadow">
                Solo vincular
              </button>
              <button onClick={() => saveModifiedToKb(item.id)} disabled={savingKbId === item.id || !kbTopicInput}
                className="px-4 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 transition shadow">
                {savingKbId === item.id ? 'Guardando...' : 'Guardar + Traducir'}
              </button>
            </div>
          </div>
          <div className="text-xs text-gray-400">
            Enviado por {item.reviewed_by} el {formatDate(item.sent_at)}
          </div>
        </div>
      ) : (
        <div className="p-4 bg-gray-50">
          {/* Pregunta del usuario - globo izquierda */}
          <div className="mb-3 flex justify-start">
            <div className="max-w-[85%] bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
              <div className="text-xs text-gray-400 font-semibold mb-1">CLIENTE {item.original_language && item.original_language !== 'es' && <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-mono ml-1">{item.original_language.toUpperCase()}</span>}</div>
              <div className="text-sm text-gray-800 whitespace-pre-wrap">{cleanText(item.original_question || '-')}</div>
              {item.metadata?.attachments && item.metadata.attachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {item.metadata.attachments.map((att, i) => (
                    att.mime_type && att.mime_type.startsWith('image/') ? (
                      <img key={i} src={'/api/alcazar/attachment/' + att.filename + '?token=' + (localStorage.getItem('token') || '')} alt="Imagen cliente"
                        className="max-w-[200px] max-h-[200px] rounded-lg border cursor-pointer hover:opacity-80 transition"
                        onClick={() => window.open('/api/alcazar/attachment/' + att.filename + '?token=' + (localStorage.getItem('token') || ''), '_blank')} />
                    ) : (
                      <a key={i} href={'/api/alcazar/attachment/' + att.filename + '?token=' + (localStorage.getItem('token') || '')} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-100 text-orange-800 border border-orange-300 rounded-lg text-xs hover:bg-orange-200 transition">
                        {att.filename || 'Adjunto'}
                      </a>
                    )
                  ))}
                </div>
              )}
              {(item.original_question || '').includes('[Imagen enviada]') && (!item.metadata?.attachments || item.metadata.attachments.length === 0) && (
                <div className="mt-2 text-xs text-gray-400 italic">El cliente envió imagen(es) pero no se guardaron los adjuntos</div>
              )}
              {!translated[item.id] && (
                <button onClick={() => translateText(item.id, item.original_question)}
                  disabled={translatingId === item.id}
                  className="mt-2 px-3 py-1.5 bg-yellow-400 text-yellow-900 border border-yellow-500 rounded-lg text-xs font-semibold hover:bg-yellow-300 disabled:opacity-50 transition">
                  {translatingId === item.id ? 'Traduciendo...' : 'Traducir'}
                </button>
              )}
              {translated[item.id] && (
                <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-sm text-gray-700 whitespace-pre-wrap">
                  <span className="text-xs font-semibold text-yellow-700 block mb-1">ES:</span>
                  {translated[item.id]}
                </div>
              )}
            </div>
          </div>
          <div className="mb-3 hidden">
            {item.metadata?.attachments && item.metadata.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {item.metadata.attachments.map((att, i) => (
                  <a key={i} href={'/api/alcazar/attachment/' + att.filename + '?token=' + (localStorage.getItem('token') || '')} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-100 text-orange-800 border border-orange-300 rounded-lg text-xs hover:bg-orange-200 transition">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/></svg>
                    {att.name || 'Adjunto'}
                    {att.size ? ' (' + Math.round(att.size / 1024) + 'KB)' : ''}
                  </a>
                ))}
              </div>
            )}
            {translated[item.id] && (
              <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                <span className="text-xs font-semibold text-yellow-700 uppercase block mb-1">Traduccion:</span>
                {translated[item.id]}
              </div>
            )}
          </div>
          {/* Respuesta XIA - globo derecha verde */}
          <div className="mb-3 flex justify-end">
            <div className="max-w-[85%] bg-green-100 border border-green-200 rounded-2xl rounded-tr-sm px-4 py-3 shadow-sm">
              <div className="text-xs text-green-600 font-semibold mb-1 flex items-center gap-2">
                XIA
                {item.xia_intent && <span className="bg-green-200 text-green-700 px-1.5 py-0.5 rounded font-mono text-xs">{item.xia_intent}</span>}
                {item.xia_confidence > 0 && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">{Math.round(item.xia_confidence * 100)}%</span>}
                {item.metadata?.kb_id && <span className="bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded font-mono text-xs">KB #{item.metadata.kb_id}</span>}
              </div>
              <div className="text-sm text-gray-800 whitespace-pre-wrap">{responseOverride[item.id] || item.edited_response || item.xia_response_es || '-'}</div>
              {responseOverride[item.id] && <span className="text-xs text-green-600 font-semibold mt-1 block">Respuesta del topic seleccionado</span>}
              {!translated['xia_' + item.id] && (
                <button onClick={() => translateText('xia_' + item.id, item.edited_response || item.xia_response_es)}
                  disabled={translatingId === 'xia_' + item.id}
                  className="mt-2 px-3 py-1.5 bg-yellow-400 text-yellow-900 border border-yellow-500 rounded-lg text-xs font-semibold hover:bg-yellow-300 disabled:opacity-50 transition">
                  {translatingId === 'xia_' + item.id ? 'Traduciendo...' : 'Traducir'}
                </button>
              )}
              {translated['xia_' + item.id] && (
                <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-sm text-gray-700 whitespace-pre-wrap">
                  <span className="text-xs font-semibold text-yellow-700 block mb-1">ES:</span>
                  {translated['xia_' + item.id]}
                </div>
              )}
            </div>
          </div>
          {item.status === 'pending' && isEditor && (
            <div className="mt-2 space-y-3">
              {/* Selector de topic KB */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <label className="block text-xs text-amber-700 font-semibold mb-1.5">Vincular a respuesta de Conocimiento (opcional)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    list={'kb-topics-' + item.id}
                    placeholder="Escribe topic o selecciona..."
                    value={topicOverride[item.id] !== undefined ? topicOverride[item.id] : (item.xia_intent || '')}
                    onChange={e => handleTopicChange(item.id, e.target.value)}
                    onKeyDown={e => e.stopPropagation()}
                    className="flex-1 border-2 border-amber-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                  <datalist id={'kb-topics-' + item.id}>
                    {kbTopics.map(t => <option key={t.id || t} value={t.topic || t}>{(t.id || '') + ' - ' + (t.topic || t)}</option>)}
                  </datalist>
                </div>
                <p className="text-xs text-amber-600 mt-1">Si XIA matcheo mal, selecciona el topic correcto. Solo mejora el matching, NO sobreescribe la respuesta.</p>
              </div>
              {/* Botones */}
              <div className="flex gap-3 flex-wrap">
                <button onClick={() => { setSaveToKb(true); setSaveTopic(topicOverride[item.id] || item.xia_intent || ''); sendItem(item.id); }} disabled={sending === item.id}
                  className="flex-1 px-6 py-3.5 bg-green-600 text-white rounded-xl text-base font-bold hover:bg-green-700 disabled:opacity-50 transition shadow-lg hover:shadow-xl hover:-translate-y-0.5">
                  {sending === item.id ? 'Enviando...' : 'APROBAR Y ENVIAR'}
                </button>
                <button onClick={() => startEdit(item)}
                  className="px-5 py-3.5 bg-blue-100 text-blue-700 rounded-xl text-sm font-semibold hover:bg-blue-200 transition">
                  Editar
                </button>
                <button onClick={() => rejectItem(item.id)}
                  className="px-5 py-3.5 bg-red-50 text-red-600 rounded-xl text-sm hover:bg-red-100 transition">
                  Rechazar
                </button>
              </div>
            </div>
          )}
          {item.status === 'sent' && (
            <div className="space-y-2">
              <div className="text-xs text-green-600">
                Enviado por {item.reviewed_by} el {formatDate(item.sent_at)}
                {item.saved_to_kb && <span className="ml-2 bg-green-100 px-2 py-0.5 rounded">KB: {item.kb_topic}</span>}
              </div>
              {isEditor && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <label className="block text-xs text-amber-700 font-semibold mb-1.5">Guardar en Conocimiento (escribe otro topic si quieres)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      list={'sent-kb-topics-' + item.id}
                      placeholder="Escribe topic o selecciona..."
                      value={sentKbTopic[item.id] !== undefined ? sentKbTopic[item.id] : (item.xia_intent || '')}
                      onChange={e => setSentKbTopic(prev => ({ ...prev, [item.id]: e.target.value }))}
                      onKeyDown={e => e.stopPropagation()}
                      className="flex-1 border-2 border-amber-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 outline-none"
                    />
                    <datalist id={'sent-kb-topics-' + item.id}>
                      {kbTopics.map(t => <option key={t.id || t} value={t.topic || t}>{(t.id || '') + ' - ' + (t.topic || t)}</option>)}
                    </datalist>
                    <button onClick={() => saveSentToKb(item.id, item.xia_intent)}
                      disabled={savingSentKb === item.id}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 transition shadow whitespace-nowrap">
                      {savingSentKb === item.id ? 'Guardando...' : 'Guardar + Traducir'}
                    </button>
                  </div>
                  <p className="text-xs text-amber-600 mt-1">Guarda la respuesta enviada bajo ese topic (lo crea si es nuevo) y la traduce a 13 idiomas.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {historyPhone && (
        <WhatsAppChatView
          phone={historyPhone}
          messages={history}
          loading={historyLoading}
          onClose={() => setHistoryPhone(null)}
        />
      )}

      <div className={'rounded-2xl p-6 border-2 flex items-center justify-between transition ' + (
        supervised ? 'bg-green-50 border-green-400' : 'bg-gray-50 border-gray-300'
      )}>
        <div>
          <div className="font-bold text-xl">{supervised ? 'AGENTE EN DIRECTO' : 'AGENTE PARADO'}</div>
          <div className="text-sm text-gray-600 mt-1">
            {supervised
              ? 'Todas las preguntas pasan por aqui. Revisa y envia.'
              : 'XIA responde automatico. Lo que no sepa va a Reenviadas.'}
          </div>
        </div>
        {isEditor && (
          <button onClick={toggleMode}
            className={'px-8 py-4 rounded-xl font-bold text-white text-lg transition shadow-lg ' + (
              supervised ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
            )}>
            {supervised ? 'STOP' : 'PLAY'}
          </button>
        )}
      </div>

      {toast && (
        <div className={'fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-lg text-white font-medium ' + (
          toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        )}>{toast.msg}</div>
      )}

      <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3 flex-wrap">
        <button onClick={() => setSubTab('pending')}
          className={'px-4 py-2 rounded-xl font-medium text-sm transition ' + (
            subTab === 'pending' ? 'bg-amber-600 text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
          Pendientes
        </button>
        <button onClick={() => setSubTab('sent')}
          className={'px-4 py-2 rounded-xl font-medium text-sm transition ' + (
            subTab === 'sent' ? 'bg-green-600 text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
          Contestadas
        </button>
        <button onClick={() => setSubTab('modified')}
          className={'px-4 py-2 rounded-xl font-medium text-sm transition ' + (
            subTab === 'modified' ? 'bg-amber-500 text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
          Modificadas
        </button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-400">Canal:</span>
          {[{k:'all',l:'Todos'},{k:'whatsapp',l:'WhatsApp'},{k:'email',l:'Email'}].map(f => (
            <button key={f.k} onClick={() => setChannelFilter(f.k)}
              className={'px-3 py-1.5 rounded-lg text-xs font-medium transition ' + (
                channelFilter === f.k ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
              {f.l}
            </button>
          ))}
          <button onClick={() => subTab === 'modified' ? fetchModified() : fetchQueue()}
            className="text-sm text-blue-600 hover:text-blue-800 transition ml-2">
            Actualizar
          </button>
        </div>
      </div>

      {subTab === 'modified' ? (
        modifiedItems.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
            No hay respuestas modificadas pendientes de guardar en Conocimiento
          </div>
        ) : (
          <div className="space-y-3">
            {modifiedItems.filter(i => channelFilter === 'all' || i.channel === channelFilter).map(item => renderItem(item, true))}
          </div>
        )
      ) : loading ? (
        <div className="bg-white rounded-xl shadow p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-3 text-gray-500">Cargando...</p>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
          {subTab === 'pending'
            ? (supervised ? 'No hay respuestas pendientes de revision' : 'Modo automatico')
            : 'No hay contestadas'}
        </div>
      ) : (
        <div className="space-y-3">
          {items.filter(i => channelFilter === 'all' || i.channel === channelFilter).map(item => renderItem(item, false))}
        </div>
      )}
    </div>
  );
}

export default AlcazarAgente;
